import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Collapse, Text } from '@chakra-ui/core'
import { Button } from 'components/ui'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { WidgetWrapper } from 'components/TasksV2/UI/WidgetWrapper'
import MediaGallery from 'components/ui/MediaGallery'
import useLocaleText from 'components/useLocaleText'
import {
  RichTextEditor,
  EditorContent,
  isEditorContentEmpty,
  textToEditorContent,
} from 'components/CommonComponents/RichTextEditor'

const MAX_COLLAPSED_HEIGHT = 150

interface Props {
  desc: string
  rich_text?: EditorContent | null
  videos: string[]
  images: string[]
  files: string[]
}

export const TaskDescription: React.FC<Props> = ({ desc, rich_text, videos, images, files }) => {
  const hasRichText = !!(rich_text && !isEditorContentEmpty(rich_text))
  const hasText = !!desc?.trim()
  const hasMedia = !!(images?.length || files?.length || videos?.length)
  const hasContent = hasRichText || hasText || hasMedia

  // Always pass EditorContent to RichTextEditor:
  // prefer rich_text (JSON) if present, otherwise convert plain text string to JSON
  const editorValue = useMemo<EditorContent | null>(
    () => (hasRichText ? rich_text! : hasText ? textToEditorContent(desc) : null),
    [hasRichText, rich_text, hasText, desc]
  )

  const [isExpanded, setIsExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)

  // ref is on the inner div (always natural height) — no isExpandedRef needed
  const contentRef = useRef<HTMLDivElement>(null)

  const t_see_more = useLocaleText('see_more')
  const t_see_less = useLocaleText('show_less')

  const toggleExpand = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setIsExpanded((prev) => !prev)
  }, [])

  // Inner div always has natural height → measure directly, no expanded-state guard needed
  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    const checkOverflow = () => {
      setIsOverflowing(el.scrollHeight > MAX_COLLAPSED_HEIGHT)
    }

    const timer = setTimeout(checkOverflow, 0)
    const observer = new ResizeObserver(checkOverflow)
    observer.observe(el)

    return () => {
      clearTimeout(timer)
      observer.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desc, rich_text])

  if (!hasContent) return null

  return (
    <WidgetWrapper mt="15px" p="15px">
      {editorValue && (
        <Box bg="white" borderRadius="12px" overflow="hidden" mb={hasMedia ? '12px' : '0'}>
          {/* position:relative for the gradient overlay */}
          <Box position="relative">
            <Collapse startingHeight={MAX_COLLAPSED_HEIGHT} isOpen={isExpanded}>
              <Box ref={contentRef as any}>
                <RichTextEditor
                  value={editorValue}
                  onChange={() => {}}
                  editable={false}
                  showToolbar={false}
                  containerStyle={{ border: 'none', backgroundColor: 'transparent', minHeight: 'auto' }}
                />
              </Box>
            </Collapse>

            {/* Gradient fade — sits outside Collapse, only when collapsed and overflowing */}
            {!isExpanded && isOverflowing && (
              <Box
                position="absolute"
                bottom="0"
                left="0"
                right="0"
                height="56px"
                style={{
                  background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)',
                  pointerEvents: 'none',
                }}
              />
            )}
          </Box>

          {/* Expand / Collapse toggle — only when content actually overflows */}
          {isOverflowing && (
            <Button
              type="button"
              variant="ghost"
              w="100%"
              h="auto"
              aria-expanded={isExpanded}
              alignItems="center"
              justifyContent="center"
              py="8px"
              cursor="pointer"
              borderTop="1px solid #E5E7EB"
              onClick={toggleExpand}
              _hover={{ bg: '#F3F4F6' }}
              style={{ gap: '4px' }}
            >
              <Text fontSize="13px" fontWeight="600" color="#6B7280">
                {isExpanded ? t_see_less || 'See less' : t_see_more || 'See more'}
              </Text>
              {isExpanded ? <ChevronUp size={16} color="#6B7280" /> : <ChevronDown size={16} color="#6B7280" />}
            </Button>
          )}
        </Box>
      )}

      <MediaGallery attachedImages={images} attachedVideos={videos} attachedFiles={files} isShowRemoveIcon={false} />
    </WidgetWrapper>
  )
}
